import React, {
    useCallback,
    useEffect,
    useMemo,
    useState
} from 'react';
import {
    Button,
    ColorPicker,
    Empty,
    Form,
    Input,
    Modal,
    Popconfirm,
    Select,
    Space,
    Switch,
    Table,
    Typography,
    message
} from 'antd';
import {
    DeleteOutlined,
    EditOutlined,
    PlusOutlined,
    ReloadOutlined,
    SaveOutlined
} from '@ant-design/icons';
import Layout from './Layout';
import { tmApi } from '../utils/taskManager';

const { Title, Text } = Typography;

const DEFAULT_COLOR = '#2f6fed';

const toHex = value => {
    if (!value) return DEFAULT_COLOR;
    if (typeof value === 'string') return value;
    if (typeof value.toHexString === 'function') {
        return value.toHexString();
    }
    return DEFAULT_COLOR;
};

const emptyRule = roleId => ({
    role_id: Number(roleId),
    can_view: false,
    can_take: false,
    owner_stage_names: [],
    owner_work_types: []
});

const TaskManagerSitesPage = () => {
    const [loading, setLoading] = useState(false);
    const [savingRules, setSavingRules] = useState(false);
    const [rows, setRows] = useState([]);
    const [roles, setRoles] = useState([]);
    const [statuses, setStatuses] = useState([]);
    const [workTypes, setWorkTypes] = useState([]);
    const [selectedId, setSelectedId] = useState(null);
    const [rulesDraft, setRulesDraft] = useState([]);
    const [rulesDirty, setRulesDirty] = useState(false);

    const [open, setOpen] = useState(false);
    const [editing, setEditing] = useState(null);
    const [form] = Form.useForm();

    const loadSites = useCallback(async () => {
        setLoading(true);
        try {
            const sitesRes = await tmApi.get(
                '/api/task-manager/sites',
                { params: { active: '0' } }
            );
            setRows(sitesRes.data?.data || []);
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки участков'
            );
        } finally {
            setLoading(false);
        }
    }, []);

    const loadMeta = useCallback(async () => {
        try {
            const [rolesRes, statusesRes, accessRes] =
                await Promise.all([
                    tmApi.get('/api/task-manager/roles'),
                    tmApi.get('/api/task-manager/statuses'),
                    tmApi
                        .get(
                            '/api/task-manager/access-constructor'
                        )
                        .catch(() => ({ data: { data: {} } }))
                ]);
            setRoles(rolesRes.data?.data || []);
            setStatuses(statusesRes.data?.data || []);
            setWorkTypes(
                accessRes.data?.data?.work_types || []
            );
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка загрузки ролей и справочников'
            );
        }
    }, []);

    const loadRules = useCallback(
        async siteId => {
            if (!siteId) {
                setRulesDraft([]);
                setRulesDirty(false);
                return;
            }
            try {
                const { data } = await tmApi.get(
                    `/api/task-manager/sites/${siteId}/role-rules`
                );
                const saved = data?.data || [];
                const byRole = new Map(
                    saved.map(r => [Number(r.role_id), r])
                );
                const draft = (roles.length
                    ? roles
                    : saved.map(r => ({
                          id: r.role_id,
                          name: `Роль #${r.role_id}`
                      }))
                ).map(role => {
                    const existing = byRole.get(Number(role.id));
                    if (!existing) {
                        return emptyRule(role.id);
                    }
                    return {
                        role_id: Number(role.id),
                        can_view: Boolean(existing.can_view),
                        can_take: Boolean(existing.can_take),
                        owner_stage_names: Array.isArray(
                            existing.owner_stage_names
                        )
                            ? existing.owner_stage_names
                            : [],
                        owner_work_types: Array.isArray(
                            existing.owner_work_types
                        )
                            ? existing.owner_work_types
                            : []
                    };
                });
                setRulesDraft(draft);
                setRulesDirty(false);
            } catch (error) {
                message.error(
                    error?.response?.data?.message ||
                        'Ошибка загрузки прав участка'
                );
            }
        },
        [roles]
    );

    useEffect(() => {
        loadSites();
        loadMeta();
    }, [loadSites, loadMeta]);

    useEffect(() => {
        if (selectedId) {
            loadRules(selectedId);
        } else {
            setRulesDraft([]);
            setRulesDirty(false);
        }
    }, [selectedId, loadRules]);

    const selectedSite = useMemo(
        () => rows.find(r => Number(r.id) === Number(selectedId)),
        [rows, selectedId]
    );

    const stageOptions = useMemo(
        () =>
            (statuses || []).map(s => ({
                value: s.name,
                label: s.name
            })),
        [statuses]
    );

    const workTypeOptions = useMemo(
        () =>
            (workTypes || []).map(w => {
                const value =
                    w.value ||
                    w.data?.value ||
                    w.label ||
                    w.data?.label;
                const label =
                    w.label ||
                    w.data?.label ||
                    w.value ||
                    value;
                return { value, label };
            }).filter(o => o.value),
        [workTypes]
    );

    const roleNameById = useMemo(() => {
        const map = new Map();
        (roles || []).forEach(r => {
            map.set(Number(r.id), r.name || r.code || `#${r.id}`);
        });
        return map;
    }, [roles]);

    const openCreate = () => {
        setEditing(null);
        form.resetFields();
        form.setFieldsValue({
            color: DEFAULT_COLOR,
            is_active: true
        });
        setOpen(true);
    };

    const openEdit = row => {
        setEditing(row);
        form.setFieldsValue({
            name: row.name,
            code: row.code,
            color: row.color || DEFAULT_COLOR,
            is_active: row.is_active !== false
        });
        setOpen(true);
    };

    const save = async () => {
        try {
            const values = await form.validateFields();
            const payload = {
                name: values.name,
                code: values.code || null,
                color: toHex(values.color),
                is_active: values.is_active !== false
            };
            if (editing?.id) {
                await tmApi.put(
                    `/api/task-manager/sites/${editing.id}`,
                    payload
                );
                message.success('Участок обновлён');
            } else {
                const { data } = await tmApi.post(
                    '/api/task-manager/sites',
                    payload
                );
                message.success('Участок создан');
                if (data?.data?.id) {
                    setSelectedId(data.data.id);
                }
            }
            setOpen(false);
            loadSites();
        } catch (error) {
            if (error?.errorFields) return;
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения участка'
            );
        }
    };

    const remove = async id => {
        try {
            await tmApi.delete(`/api/task-manager/sites/${id}`);
            message.success('Участок деактивирован');
            if (Number(selectedId) === Number(id)) {
                setSelectedId(null);
            }
            loadSites();
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка удаления участка'
            );
        }
    };

    const patchRule = (roleId, patch) => {
        setRulesDraft(prev =>
            prev.map(rule =>
                Number(rule.role_id) === Number(roleId)
                    ? { ...rule, ...patch }
                    : rule
            )
        );
        setRulesDirty(true);
    };

    const saveRules = async () => {
        if (!selectedId) return;
        setSavingRules(true);
        try {
            await tmApi.put(
                `/api/task-manager/sites/${selectedId}/role-rules`,
                { rules: rulesDraft }
            );
            message.success('Права участка сохранены');
            setRulesDirty(false);
            await loadRules(selectedId);
        } catch (error) {
            message.error(
                error?.response?.data?.message ||
                    'Ошибка сохранения прав'
            );
        } finally {
            setSavingRules(false);
        }
    };

    const selectSite = record => {
        if (
            rulesDirty &&
            Number(selectedId) !== Number(record.id)
        ) {
            Modal.confirm({
                title: 'Есть несохранённые изменения',
                content:
                    'Переключить участок без сохранения прав?',
                okText: 'Переключить',
                cancelText: 'Отмена',
                onOk: () => setSelectedId(record.id)
            });
            return;
        }
        setSelectedId(record.id);
    };

    return (
        <Layout>
            <div style={{ padding: 16 }}>
                <Space
                    style={{
                        width: '100%',
                        justifyContent: 'space-between',
                        marginBottom: 16
                    }}
                >
                    <div>
                        <Title level={3} style={{ margin: 0 }}>
                            Участки
                        </Title>
                        <Text type="secondary">
                            Слева список участков, справа —
                            права ролей на выбранном участке
                        </Text>
                    </div>
                    <Space>
                        <Button
                            icon={<ReloadOutlined />}
                            onClick={() => {
                                loadSites();
                                loadMeta();
                                if (selectedId) {
                                    loadRules(selectedId);
                                }
                            }}
                        >
                            Обновить
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={openCreate}
                        >
                            Добавить
                        </Button>
                    </Space>
                </Space>

                <div
                    style={{
                        display: 'grid',
                        gridTemplateColumns:
                            'minmax(320px, 1fr) minmax(420px, 1.4fr)',
                        gap: 16,
                        alignItems: 'start'
                    }}
                >
                    <div
                        style={{
                            border: '1px solid #f0f0f0',
                            borderRadius: 8,
                            overflow: 'hidden',
                            background: '#fff'
                        }}
                    >
                        <Table
                            rowKey="id"
                            size="small"
                            loading={loading}
                            dataSource={rows}
                            pagination={false}
                            scroll={{ y: 'calc(100vh - 220px)' }}
                            onRow={record => ({
                                onClick: () => selectSite(record),
                                style: {
                                    cursor: 'pointer',
                                    background:
                                        Number(selectedId) ===
                                        Number(record.id)
                                            ? '#e6f4ff'
                                            : undefined
                                }
                            })}
                            columns={[
                                {
                                    title: '',
                                    dataIndex: 'color',
                                    width: 40,
                                    render: color => (
                                        <span
                                            style={{
                                                display:
                                                    'inline-block',
                                                width: 14,
                                                height: 14,
                                                borderRadius: 3,
                                                background:
                                                    color ||
                                                    DEFAULT_COLOR,
                                                border: '1px solid #d9d9d9'
                                            }}
                                        />
                                    )
                                },
                                {
                                    title: 'Название',
                                    dataIndex: 'name',
                                    ellipsis: true
                                },
                                {
                                    title: 'Код',
                                    dataIndex: 'code',
                                    width: 90,
                                    render: v => v || '—'
                                },
                                {
                                    title: 'Акт.',
                                    dataIndex: 'is_active',
                                    width: 60,
                                    render: v =>
                                        v === false ? 'нет' : 'да'
                                },
                                {
                                    title: '',
                                    key: 'actions',
                                    width: 72,
                                    render: (_, row) => (
                                        <Space
                                            size={4}
                                            onClick={e =>
                                                e.stopPropagation()
                                            }
                                        >
                                            <Button
                                                type="text"
                                                size="small"
                                                icon={
                                                    <EditOutlined />
                                                }
                                                onClick={() =>
                                                    openEdit(row)
                                                }
                                            />
                                            <Popconfirm
                                                title="Деактивировать участок?"
                                                onConfirm={() =>
                                                    remove(row.id)
                                                }
                                            >
                                                <Button
                                                    type="text"
                                                    size="small"
                                                    danger
                                                    icon={
                                                        <DeleteOutlined />
                                                    }
                                                />
                                            </Popconfirm>
                                        </Space>
                                    )
                                }
                            ]}
                        />
                    </div>

                    <div
                        style={{
                            border: '1px solid #f0f0f0',
                            borderRadius: 8,
                            background: '#fff',
                            padding: 16,
                            minHeight: 360
                        }}
                    >
                        {!selectedSite ? (
                            <Empty
                                description="Выберите участок слева"
                                style={{ marginTop: 80 }}
                            />
                        ) : (
                            <>
                                <Space
                                    style={{
                                        width: '100%',
                                        justifyContent:
                                            'space-between',
                                        marginBottom: 12
                                    }}
                                >
                                    <div>
                                        <Title
                                            level={4}
                                            style={{ margin: 0 }}
                                        >
                                            {selectedSite.name}
                                        </Title>
                                        <Text type="secondary">
                                            Видимость, взятие и
                                            владение по этапам /
                                            видам работ
                                        </Text>
                                    </div>
                                    <Button
                                        type="primary"
                                        icon={<SaveOutlined />}
                                        loading={savingRules}
                                        disabled={!rulesDirty}
                                        onClick={saveRules}
                                    >
                                        Сохранить права
                                    </Button>
                                </Space>

                                <Table
                                    rowKey="role_id"
                                    size="small"
                                    pagination={false}
                                    dataSource={rulesDraft}
                                    scroll={{
                                        x: 760,
                                        y: 'calc(100vh - 280px)'
                                    }}
                                    columns={[
                                        {
                                            title: 'Роль',
                                            dataIndex: 'role_id',
                                            width: 160,
                                            fixed: 'left',
                                            render: id =>
                                                roleNameById.get(
                                                    Number(id)
                                                ) || `#${id}`
                                        },
                                        {
                                            title: 'Виден',
                                            dataIndex: 'can_view',
                                            width: 90,
                                            render: (v, row) => (
                                                <Switch
                                                    checked={Boolean(
                                                        v
                                                    )}
                                                    onChange={checked =>
                                                        patchRule(
                                                            row.role_id,
                                                            {
                                                                can_view:
                                                                    checked,
                                                                ...(checked
                                                                    ? {}
                                                                    : {
                                                                          can_take: false
                                                                      })
                                                            }
                                                        )
                                                    }
                                                />
                                            )
                                        },
                                        {
                                            title: 'Может взять',
                                            dataIndex: 'can_take',
                                            width: 110,
                                            render: (v, row) => (
                                                <Switch
                                                    checked={Boolean(
                                                        v
                                                    )}
                                                    disabled={
                                                        !row.can_view
                                                    }
                                                    onChange={checked =>
                                                        patchRule(
                                                            row.role_id,
                                                            {
                                                                can_take:
                                                                    checked
                                                            }
                                                        )
                                                    }
                                                />
                                            )
                                        },
                                        {
                                            title: 'Владение: этапы',
                                            dataIndex:
                                                'owner_stage_names',
                                            render: (v, row) => (
                                                <Select
                                                    mode="multiple"
                                                    allowClear
                                                    placeholder="Этапы"
                                                    style={{
                                                        width: '100%',
                                                        minWidth: 180
                                                    }}
                                                    options={
                                                        stageOptions
                                                    }
                                                    value={v || []}
                                                    onChange={vals =>
                                                        patchRule(
                                                            row.role_id,
                                                            {
                                                                owner_stage_names:
                                                                    vals
                                                            }
                                                        )
                                                    }
                                                    maxTagCount="responsive"
                                                />
                                            )
                                        },
                                        {
                                            title: 'Владение: виды работ',
                                            dataIndex:
                                                'owner_work_types',
                                            render: (v, row) => (
                                                <Select
                                                    mode="multiple"
                                                    allowClear
                                                    placeholder="Виды работ"
                                                    style={{
                                                        width: '100%',
                                                        minWidth: 160
                                                    }}
                                                    options={
                                                        workTypeOptions
                                                    }
                                                    value={v || []}
                                                    onChange={vals =>
                                                        patchRule(
                                                            row.role_id,
                                                            {
                                                                owner_work_types:
                                                                    vals
                                                            }
                                                        )
                                                    }
                                                    maxTagCount="responsive"
                                                />
                                            )
                                        }
                                    ]}
                                />
                            </>
                        )}
                    </div>
                </div>

                <Modal
                    title={
                        editing
                            ? `Участок #${editing.id}`
                            : 'Новый участок'
                    }
                    open={open}
                    onCancel={() => setOpen(false)}
                    onOk={save}
                    okText="Сохранить"
                    destroyOnClose
                >
                    <Form form={form} layout="vertical">
                        <Form.Item
                            name="name"
                            label="Название"
                            rules={[
                                {
                                    required: true,
                                    message: 'Укажите название'
                                }
                            ]}
                        >
                            <Input />
                        </Form.Item>
                        <Form.Item name="code" label="Код">
                            <Input />
                        </Form.Item>
                        <Form.Item name="color" label="Цвет">
                            <ColorPicker
                                showText
                                format="hex"
                                disabledAlpha
                            />
                        </Form.Item>
                        <Form.Item
                            name="is_active"
                            label="Активен"
                            valuePropName="checked"
                        >
                            <Switch />
                        </Form.Item>
                    </Form>
                </Modal>
            </div>
        </Layout>
    );
};

export default TaskManagerSitesPage;
